import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BookOpen } from "lucide-react";
import axios from "../../api/axios";

// Import central theme classes from MasterCssClass.js
import {
  masterPanelCard,
  masterHeadingMain,
  masterTableWrap,
  masterTable,
  masterTableHead,
  masterTableRow,
  masterPageContainer,
  masterHeaderContent,
  masterHeaderBrand,
  masterHeaderIconBox,
  masterTableContainer,
  masterThCode,
  masterThName,
  masterTdCode,
  masterTdName,
  masterTdEmpty,
} from "../../GlobalComponents/MasterCssClass";

const SyllabusView = () => {
  const { syllabusId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [syllabus, setSyllabus] = useState(null);

  useEffect(() => {
    const loadSyllabus = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`/master/syllabus/${syllabusId}`);
        if (res.data?.success) {
          setSyllabus(res.data.data);
        } else {
          setError("SYLLABUS NOT FOUND");
        }
      } catch (err) {
        setError(err?.response?.data?.message || "FAILED TO LOAD SYLLABUS");
      } finally {
        setLoading(false);
      }
    };

    if (syllabusId) loadSyllabus();
  }, [syllabusId]);

  if (loading) return <div className="p-4 text-xs font-bold text-slate-500 animate-pulse">Loading syllabus...</div>;
  if (error) return <div className="p-4 text-xs font-bold text-red-600 bg-red-50 rounded-lg">{error}</div>;
  if (!syllabus) return <div className="p-4 text-xs font-bold text-slate-500">No data found</div>;

  const getSubTopicName = (subTopic) =>
    typeof subTopic === "string" ? subTopic : String(subTopic?.subTopicName || subTopic?.name || "");
  const getNestedTopicName = (nestedTopic) =>
    typeof nestedTopic === "string" ? nestedTopic : String(nestedTopic?.nestedTopicName || nestedTopic?.name || "");
  const getNestedSubTopicName = (nestedSubTopic) =>
    typeof nestedSubTopic === "string" ? nestedSubTopic : String(nestedSubTopic?.nestedSubTopicName || nestedSubTopic?.name || "");

  return (
    <div className={`${masterPageContainer} h-full flex flex-col overflow-y-auto custom-scrollbar p-1 space-y-1`}>
      {/* HEADER CARD */}
      <div className={`${masterPanelCard} p-1.5`}>
        <div className={masterHeaderContent}>
          <div className={masterHeaderBrand}>
            <div className={masterHeaderIconBox}>
              <BookOpen size={13} />
            </div>
            <div>
              <h1 className={masterHeadingMain}>{syllabus.subjectName}</h1>
              <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider mt-0.5">
                {syllabus.examName} <span className="mx-1.5 text-slate-300">|</span> {syllabus.catName}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* TABLE CONTAINER */}
      <div className={`${masterTableContainer} flex-1 min-h-0 flex flex-col p-1`}>
        <div className={`${masterTableWrap} overflow-x-auto overflow-y-auto custom-scrollbar flex-1 max-h-[calc(100vh-210px)]`}>
          <table className={masterTable}>
            <thead>
              <tr className={masterTableHead}>
                <th className={`${masterThCode} w-16`}>SL</th>
                <th className={`${masterThName} w-1/3`}>TOPIC NAME</th>
                <th className={masterThName}>DETAILS / SUB-TOPICS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {(syllabus.topics || []).length > 0 ? (
                (syllabus.topics || []).map((topic, idx) => (
                  <tr key={idx} className={masterTableRow}>
                    <td className={`${masterTdCode} font-bold text-slate-600`}>
                      {String(idx + 1).padStart(2, "0")}
                    </td>
                    <td className={`${masterTdName} font-bold text-slate-900 uppercase`}>
                      {topic.topicName}
                    </td>
                    <td className={masterTdName}>
                      <ul className="space-y-0.5 py-1">
                        {(topic.subTopics || []).map((sub, sIdx) => (
                          <li key={sIdx} className="text-xs text-slate-600 font-semibold">
                            <div className="flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0"></span>
                              <span className="leading-tight">{getSubTopicName(sub) || "---"}</span>
                            </div>
                            {(sub?.nestedTopics || sub?.childTopics || []).length > 0 && (
                              <ul className="ml-5 mt-1 space-y-1">
                                {(sub.nestedTopics || sub.childTopics || []).map((nestedTopic, nIdx) => (
                                  <li key={`${sIdx}-${nIdx}`}>
                                    <div className="flex items-center gap-2 text-[11px] text-slate-500">
                                      <span className="w-1 h-1 rounded-full bg-emerald-500 shrink-0"></span>
                                      <span>{getNestedTopicName(nestedTopic) || "---"}</span>
                                    </div>
                                    {(nestedTopic?.nestedSubTopics || nestedTopic?.childTopics || []).length > 0 && (
                                      <ul className="ml-5 mt-0.5 space-y-0.5">
                                        {(nestedTopic.nestedSubTopics || nestedTopic.childTopics || []).map((nestedSubTopic, ssIdx) => (
                                          <li key={`${sIdx}-${nIdx}-${ssIdx}`} className="flex items-center gap-2 text-[10px] text-slate-400">
                                            <span className="w-1 h-1 rounded-full bg-slate-400 shrink-0"></span>
                                            <span>{getNestedSubTopicName(nestedSubTopic) || "---"}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className={masterTdEmpty}>No Topics Found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SyllabusView;
